// Deterministic scope grammar for parameterized search deeplinks (BUIL-541).
// "orders canceled last month paypal" / "members overdue" / "products rtp" /
// "group bookings overdue" / "schedule tomorrow day ticket" become filtered
// list views. Only known presets/enums match — no free-text guessing — and
// the produced row's subtitle always states the full interpreted scope, so
// the user sees exactly what they will get before opening it. If any words
// stay unrecognized (and don't resolve to a product or venue location for
// intents that support those joins), no row is offered — never silently
// drop words.
//
// Synonym tables below map typed phrases to filter slugs; the LABELS shown in
// row subtitles come from the same filter configs the destination pages
// render, so the preview always matches what the user sees after opening.
import { DateRangeName } from 'dateRanges'
import { categoryOptions as cartFilterOptions } from 'orders/cart/filters/config'
import { categoryOptions as orderFilterOptions } from 'orders/orders/filters/config'
import { VIA_SEARCH_PARAM } from 'uiComponents/searchScopeBadge'
import { PageResult } from './pageDirectory'
import { addDays } from 'date-fns'

const labelBySlug = (options: { name: string; slug: string }[]): Record<string, string> =>
    options.reduce<Record<string, string>>((acc, option) => ({ ...acc, [option.slug]: option.name }), {})

const ORDER_STATUS_LABELS = labelBySlug(orderFilterOptions.status)
const PAYMENT_METHOD_LABELS = labelBySlug(orderFilterOptions.payment_method)
const EMAIL_STATUS_LABELS = labelBySlug(orderFilterOptions.email_status)
const RESERVATION_STATUS_LABELS = labelBySlug(cartFilterOptions.reservation_status)

export type ScopeIntent = 'orders' | 'memberships' | 'products' | 'reservations' | 'schedule'

interface DatePreset {
    key: DateRangeName
    label: string
}

interface EnumMatch {
    value: string
    label: string
}

// First token/phrase that maps to an intent decides the target list page.
// Phrases are checked before single tokens at each position.
const INTENT_PHRASE_MAP: Record<string, ScopeIntent> = {
    'group bookings': 'reservations',
    'group booking': 'reservations',
    'pending reservations': 'reservations',
    'pending reservation': 'reservations',
    'daily schedule': 'schedule',
}

const INTENT_TOKEN_MAP: Record<string, ScopeIntent> = {
    orders: 'orders',
    order: 'orders',
    memberships: 'memberships',
    membership: 'memberships',
    members: 'memberships',
    member: 'memberships',
    products: 'products',
    product: 'products',
    reservations: 'reservations',
    reservation: 'reservations',
    carts: 'reservations',
    cart: 'reservations',
    schedule: 'schedule',
}

// Phrase → orders page dateRange preset key (see src/app/dateRanges.ts).
export const DATE_PRESETS: Record<string, DatePreset> = {
    today: { key: 'today', label: 'today' },
    yesterday: { key: 'yesterday', label: 'yesterday' },
    tomorrow: { key: 'tomorrow', label: 'tomorrow' },
    'day after tomorrow': { key: 'dayAfterTomorrow', label: 'day after tomorrow' },
    'this week': { key: 'thisWeek', label: 'this week' },
    'last week': { key: 'lastWeek', label: 'last week' },
    'next week': { key: 'nextWeek', label: 'next week' },
    'this month': { key: 'thisMonth', label: 'this month' },
    'last month': { key: 'lastMonth', label: 'last month' },
    'next month': { key: 'nextMonth', label: 'next month' },
    'last 7 days': { key: 'last7days', label: 'last 7 days' },
    'last 30 days': { key: 'last30days', label: 'last 30 days' },
    'last 4 weeks': { key: 'last4weeks', label: 'last 4 weeks' },
    'last 90 days': { key: 'last90days', label: 'last 90 days' },
    'last 6 months': { key: 'lastSixMonths', label: 'last 6 months' },
    'this quarter': { key: 'thisQuarter', label: 'this quarter' },
    'this year': { key: 'thisYear', label: 'this year' },
    lifetime: { key: 'lifetime', label: 'lifetime' },
    'all time': { key: 'lifetime', label: 'lifetime' },
}

// Applied when the user scopes orders by status/payment/product without
// naming a range — the orders page would otherwise default to today only.
const DEFAULT_DATE_PRESET: DatePreset = DATE_PRESETS['last 30 days']

// Phrase → orders `filter=status:` slug.
const ORDER_STATUS_SYNONYMS: Record<string, string> = {
    canceled: 'CANCELED',
    cancelled: 'CANCELED',
    open: 'OPEN',
    confirmed: 'CONFIRMED',
    redeemed: 'REDEEMED',
    'partially redeemed': 'PARTIALLY_REDEEMED',
    'partially canceled': 'PARTIALLY_CANCELLED',
    'partially cancelled': 'PARTIALLY_CANCELLED',
}

// Phrase → orders `filter=payment_method:` slug.
const PAYMENT_METHOD_SYNONYMS: Record<string, string> = {
    paypal: 'paypal',
    ideal: 'ideal',
    visa: 'visa',
    mastercard: 'mc',
    'credit card': 'creditcard',
    amex: 'amex',
    sofort: 'sofort',
    giropay: 'giropay',
    bancontact: 'bancontact',
    'bank transfer': 'banktransfer',
    'gift card': 'giftcard',
    giftcard: 'giftcard',
    eps: 'eps',
    przelewy24: 'przelewy24',
}

// Phrase → orders `filter=email_status:` slug. Only the unambiguous words:
// "delivered"/"opened" would read as order states to venue staff, so they
// stay out. Bounced/dropped confirmations are the typo'd-email worklist.
const EMAIL_STATUS_SYNONYMS: Record<string, string> = {
    bounced: 'bounced',
    'email bounced': 'bounced',
    dropped: 'dropped',
    'email dropped': 'dropped',
    undelivered: 'bounced',
}

// Phrase → memberships `filter=membership_status:` value
// (memberships/membershipsTable/membershipsTableFilter.tsx).
const MEMBERSHIP_STATUSES: Record<string, EnumMatch> = {
    active: { value: 'active', label: 'Active' },
    inactive: { value: 'inactive', label: 'Inactive' },
    overdue: { value: 'overdue', label: 'Overdue' },
    'payment overdue': { value: 'overdue', label: 'Overdue' },
}

// Phrase → products flat list `pricingTypes` value (products/types.ts).
const PRICING_TYPES: Record<string, EnumMatch> = {
    rtp: { value: 'rtp', label: 'Real-time pricing' },
    'real time pricing': { value: 'rtp', label: 'Real-time pricing' },
    'real-time pricing': { value: 'rtp', label: 'Real-time pricing' },
    nyop: { value: 'nyop', label: 'Name your own price' },
    'name your own price': { value: 'nyop', label: 'Name your own price' },
    static: { value: 'static', label: 'Static pricing' },
    'fixed price': { value: 'static', label: 'Static pricing' },
    upsell: { value: 'upsell', label: 'Upsell' },
    upsells: { value: 'upsell', label: 'Upsell' },
}

// Phrase → pending reservations `filter=reservation_status:` slug.
// "Group bookings" in venue language.
const RESERVATION_STATUS_SYNONYMS: Record<string, string> = {
    open: 'open',
    pending: 'pending',
    initiated: 'initiated',
    overdue: 'overdue',
    expired: 'expired',
    cancelled: 'cancelled',
    canceled: 'cancelled',
    completed: 'completed',
}

const toEnumMatch = (slug: string, labels: Record<string, string>): EnumMatch => ({
    value: slug,
    label: labels[slug] ?? slug,
})

// Daily schedule shows one day at a time (selected_date param), so only
// single-day words apply there.
const SCHEDULE_DAYS: Record<string, { offsetDays: number; label: string }> = {
    today: { offsetDays: 0, label: 'Today' },
    tomorrow: { offsetDays: 1, label: 'Tomorrow' },
    'day after tomorrow': { offsetDays: 2, label: 'Day after tomorrow' },
    yesterday: { offsetDays: -1, label: 'Yesterday' },
}

// Longest phrase in the tables above ("name your own price").
const MAX_PHRASE_WORDS = 4

export interface ParsedScopedQuery {
    intent: ScopeIntent | null
    dateRange: DatePreset | null
    statuses: EnumMatch[]
    paymentMethods: EnumMatch[]
    emailStatuses: EnumMatch[]
    membershipStatuses: EnumMatch[]
    pricingTypes: EnumMatch[]
    reservationStatuses: EnumMatch[]
    scheduleDay: { offsetDays: number; label: string } | null
    // Words the grammar didn't recognize — for intents with a product join
    // (orders, schedule) these are candidates; elsewhere they veto the row.
    remainder: string
}

const findIntent = (words: string[]): ScopeIntent | null => {
    for (let i = 0; i < words.length; i++) {
        const phrase = words.slice(i, i + 2).join(' ')
        if (INTENT_PHRASE_MAP[phrase]) {
            return INTENT_PHRASE_MAP[phrase]
        }
        if (INTENT_TOKEN_MAP[words[i]]) {
            return INTENT_TOKEN_MAP[words[i]]
        }
    }
    return null
}

export const parseScopedQuery = (query: string): ParsedScopedQuery => {
    const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean)
    const intent = findIntent(words)

    const parsed: ParsedScopedQuery = {
        intent,
        dateRange: null,
        statuses: [],
        paymentMethods: [],
        emailStatuses: [],
        membershipStatuses: [],
        pricingTypes: [],
        reservationStatuses: [],
        scheduleDay: null,
        remainder: '',
    }
    if (!intent) {
        return parsed
    }

    const pushUnique = (list: EnumMatch[], match: EnumMatch) => {
        if (!list.some((entry) => entry.value === match.value)) {
            list.push(match)
        }
    }

    // Scope tables the detected intent's target page can actually honor.
    const matchPhrase = (phrase: string): boolean => {
        switch (intent) {
            case 'orders':
                if (DATE_PRESETS[phrase]) {
                    parsed.dateRange = parsed.dateRange ?? DATE_PRESETS[phrase]
                    return true
                }
                if (ORDER_STATUS_SYNONYMS[phrase]) {
                    pushUnique(parsed.statuses, toEnumMatch(ORDER_STATUS_SYNONYMS[phrase], ORDER_STATUS_LABELS))
                    return true
                }
                if (PAYMENT_METHOD_SYNONYMS[phrase]) {
                    pushUnique(
                        parsed.paymentMethods,
                        toEnumMatch(PAYMENT_METHOD_SYNONYMS[phrase], PAYMENT_METHOD_LABELS),
                    )
                    return true
                }
                if (EMAIL_STATUS_SYNONYMS[phrase]) {
                    pushUnique(parsed.emailStatuses, toEnumMatch(EMAIL_STATUS_SYNONYMS[phrase], EMAIL_STATUS_LABELS))
                    return true
                }
                return false
            case 'memberships':
                if (MEMBERSHIP_STATUSES[phrase]) {
                    pushUnique(parsed.membershipStatuses, MEMBERSHIP_STATUSES[phrase])
                    return true
                }
                return false
            case 'products':
                if (PRICING_TYPES[phrase]) {
                    pushUnique(parsed.pricingTypes, PRICING_TYPES[phrase])
                    return true
                }
                return false
            case 'reservations':
                if (RESERVATION_STATUS_SYNONYMS[phrase]) {
                    pushUnique(
                        parsed.reservationStatuses,
                        toEnumMatch(RESERVATION_STATUS_SYNONYMS[phrase], RESERVATION_STATUS_LABELS),
                    )
                    return true
                }
                return false
            case 'schedule':
                if (SCHEDULE_DAYS[phrase]) {
                    parsed.scheduleDay = parsed.scheduleDay ?? SCHEDULE_DAYS[phrase]
                    return true
                }
                return false
            default:
                return false
        }
    }

    const remainderWords: string[] = []
    let i = 0
    while (i < words.length) {
        const intentPhrase = words.slice(i, i + 2).join(' ')
        if (INTENT_PHRASE_MAP[intentPhrase] === intent) {
            i += 2
            continue
        }
        if (INTENT_TOKEN_MAP[words[i]] === intent) {
            i += 1
            continue
        }
        let matchedWords = 0
        for (let length = Math.min(MAX_PHRASE_WORDS, words.length - i); length > 0; length--) {
            if (matchPhrase(words.slice(i, i + length).join(' '))) {
                matchedWords = length
                break
            }
        }
        if (matchedWords) {
            i += matchedWords
        } else {
            remainderWords.push(words[i])
            i += 1
        }
    }
    parsed.remainder = remainderWords.join(' ')
    return parsed
}

export interface ScopedProduct {
    id: string
    title: string
}

export interface ScopedLocation {
    id: string
    title: string
}

// Remainder words resolved against live data by the scoped-view hook:
// product names from the federated results, location names from the
// account's location list.
export interface ScopedJoins {
    products: ScopedProduct[]
    locations: ScopedLocation[]
}

// Curated completion phrases per intent, ordered by operational value.
// Deterministic: drawn from the same tables the parser matches against.
const SUGGESTION_PHRASES: Record<ScopeIntent, string[]> = {
    orders: [
        'today',
        'this week',
        'last month',
        'canceled',
        'open',
        'bounced',
        'paypal',
        'ideal',
        'credit card',
        'yesterday',
        'last 30 days',
        'confirmed',
        'redeemed',
    ],
    memberships: ['active', 'overdue', 'inactive'],
    products: ['rtp', 'nyop', 'static', 'upsell'],
    reservations: ['open', 'pending', 'initiated', 'overdue', 'expired', 'completed'],
    schedule: ['today', 'tomorrow', 'day after tomorrow', 'yesterday'],
}

const MAX_SUGGESTIONS = 6

const isPhraseApplied = (intent: ScopeIntent, phrase: string, parsed: ParsedScopedQuery): boolean => {
    switch (intent) {
        case 'orders':
            if (DATE_PRESETS[phrase]) {
                return !!parsed.dateRange
            }
            if (ORDER_STATUS_SYNONYMS[phrase]) {
                return parsed.statuses.some((status) => status.value === ORDER_STATUS_SYNONYMS[phrase])
            }
            if (PAYMENT_METHOD_SYNONYMS[phrase]) {
                return parsed.paymentMethods.some((method) => method.value === PAYMENT_METHOD_SYNONYMS[phrase])
            }
            if (EMAIL_STATUS_SYNONYMS[phrase]) {
                return parsed.emailStatuses.some((status) => status.value === EMAIL_STATUS_SYNONYMS[phrase])
            }
            return false
        case 'memberships':
            return parsed.membershipStatuses.some((status) => status.value === MEMBERSHIP_STATUSES[phrase]?.value)
        case 'products':
            return parsed.pricingTypes.some((type) => type.value === PRICING_TYPES[phrase]?.value)
        case 'reservations':
            return parsed.reservationStatuses.some((status) => status.value === RESERVATION_STATUS_SYNONYMS[phrase])
        case 'schedule':
            return !!parsed.scheduleDay
        default:
            return false
    }
}

export interface ScopeSuggestions {
    // Trailing word the suggestions would replace ('' = they append).
    partial: string
    phrases: string[]
}

export const suggestScopePhrases = (query: string): ScopeSuggestions => {
    const parsed = parseScopedQuery(query)
    if (!parsed.intent) {
        return { partial: '', phrases: [] }
    }
    const endsWithSpace = /\s$/.test(query)
    const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean)
    const lastWord = words[words.length - 1] ?? ''
    const remainderWords = new Set(parsed.remainder.split(' ').filter(Boolean))
    const partial = !endsWithSpace && remainderWords.has(lastWord) ? lastWord : ''
    const intent = parsed.intent

    const phrases = SUGGESTION_PHRASES[intent]
        .filter((phrase) => !isPhraseApplied(intent, phrase, parsed))
        .filter((phrase) => (partial ? phrase.startsWith(partial) && phrase !== partial : true))
        .slice(0, MAX_SUGGESTIONS)

    return { partial, phrases }
}

// Intents whose target page accepts a products filter, letting unrecognized
// words resolve to product names via the federated search results.
export const intentSupportsProductJoin = (intent: ScopeIntent | null): boolean =>
    intent === 'orders' || intent === 'schedule'

// The orders page additionally accepts a location filter, so its
// unrecognized words may also resolve to venue location names.
export const intentSupportsLocationJoin = (intent: ScopeIntent | null): boolean => intent === 'orders'

const buildOrdersView = (accountSlug: string, parsed: ParsedScopedQuery, joins: ScopedJoins): PageResult | null => {
    const { products, locations } = joins
    const hasScope =
        parsed.dateRange ||
        parsed.statuses.length ||
        parsed.paymentMethods.length ||
        parsed.emailStatuses.length ||
        products.length ||
        locations.length
    if (!hasScope || (parsed.remainder && !products.length && !locations.length)) {
        return null
    }

    const dateRange = parsed.dateRange ?? DEFAULT_DATE_PRESET
    const params = new URLSearchParams()
    params.set('dateRange', dateRange.key)
    params.set('dateRangeType', 'visit')

    const filterParts: string[] = []
    if (parsed.statuses.length) {
        filterParts.push(`status:${parsed.statuses.map((status) => status.value).join(',')}`)
    }
    if (parsed.paymentMethods.length) {
        filterParts.push(`payment_method:${parsed.paymentMethods.map((method) => method.value).join(',')}`)
    }
    if (parsed.emailStatuses.length) {
        filterParts.push(`email_status:${parsed.emailStatuses.map((status) => status.value).join(',')}`)
    }
    if (products.length) {
        filterParts.push(`products:${products.map((product) => product.id).join(',')}`)
    }
    if (locations.length) {
        filterParts.push(`location_id:${locations.map((location) => location.id).join(',')}`)
    }
    if (filterParts.length) {
        params.set('filter', filterParts.join(';'))
    }

    const scopeLabels = [
        `Visiting ${dateRange.label}`,
        ...parsed.statuses.map((status) => status.label),
        ...parsed.paymentMethods.map((method) => method.label),
        ...parsed.emailStatuses.map((status) => status.label),
        ...products.map((product) => product.title),
        ...locations.map((location) => location.title),
    ]
    const subtitle = scopeLabels.join(' · ')
    params.set(VIA_SEARCH_PARAM, subtitle)

    return {
        title: 'Orders',
        subtitle,
        iconName: 'ShoppingBag',
        path: `/account/${accountSlug}/orders/orders/?${params.toString()}`,
    }
}

const buildMembershipsView = (accountSlug: string, parsed: ParsedScopedQuery): PageResult | null => {
    if (!parsed.membershipStatuses.length || parsed.remainder) {
        return null
    }
    const params = new URLSearchParams()
    params.set('filter', `membership_status:${parsed.membershipStatuses.map((status) => status.value).join(',')}`)
    const subtitle = parsed.membershipStatuses.map((status) => status.label).join(' · ')
    params.set(VIA_SEARCH_PARAM, subtitle)
    return {
        title: 'Memberships',
        subtitle,
        iconName: 'IdCard',
        path: `/account/${accountSlug}/memberships/?${params.toString()}`,
    }
}

const buildProductsView = (accountSlug: string, parsed: ParsedScopedQuery): PageResult | null => {
    if (!parsed.pricingTypes.length || parsed.remainder) {
        return null
    }
    const params = new URLSearchParams()
    params.set('pricingTypes', parsed.pricingTypes.map((type) => type.value).join(','))
    return {
        title: 'Products',
        subtitle: parsed.pricingTypes.map((type) => type.label).join(' · '),
        iconName: 'Tickets',
        path: `/account/${accountSlug}/products/home/flat?${params.toString()}`,
    }
}

const buildReservationsView = (accountSlug: string, parsed: ParsedScopedQuery): PageResult | null => {
    if (!parsed.reservationStatuses.length || parsed.remainder) {
        return null
    }
    const params = new URLSearchParams()
    params.set('filter', `reservation_status:${parsed.reservationStatuses.map((status) => status.value).join(',')}`)
    const subtitle = parsed.reservationStatuses.map((status) => status.label).join(' · ')
    params.set(VIA_SEARCH_PARAM, subtitle)
    return {
        title: 'Pending Reservations',
        subtitle,
        iconName: 'ShoppingCart',
        path: `/account/${accountSlug}/orders/pending-reservations?${params.toString()}`,
    }
}

const buildScheduleView = (
    accountSlug: string,
    parsed: ParsedScopedQuery,
    products: ScopedProduct[],
    now: Date,
): PageResult | null => {
    const hasScope = parsed.scheduleDay || products.length
    if (!hasScope || (parsed.remainder && !products.length)) {
        return null
    }
    const params = new URLSearchParams()
    const day = parsed.scheduleDay ?? SCHEDULE_DAYS.today
    if (day.offsetDays !== 0) {
        const date = addDays(now, day.offsetDays)
        params.set('selected_date', date.toISOString())
    }
    if (products.length) {
        params.set('filter', `products:${products.map((product) => product.id).join(',')}`)
    }
    const query = params.toString()
    return {
        title: 'Daily Schedule',
        subtitle: [day.label, ...products.map((product) => product.title)].join(' · '),
        iconName: 'CalendarDays',
        path: `/account/${accountSlug}/orders/daily_schedule/${query ? `?${query}` : ''}`,
    }
}

export const buildScopedView = (
    accountSlug: string,
    parsed: ParsedScopedQuery,
    joins: ScopedJoins,
    now: Date = new Date(),
): PageResult | null => {
    switch (parsed.intent) {
        case 'orders':
            return buildOrdersView(accountSlug, parsed, joins)
        case 'memberships':
            return buildMembershipsView(accountSlug, parsed)
        case 'products':
            return buildProductsView(accountSlug, parsed)
        case 'reservations':
            return buildReservationsView(accountSlug, parsed)
        case 'schedule':
            return buildScheduleView(accountSlug, parsed, joins.products, now)
        default:
            return null
    }
}
